import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { MatchCardAction, MatchListEntry } from "../../types";
import { Grid, List } from "lucide-react";
import { ListView } from "./partials/ListView";
import { GridView } from "./partials/GridView";
import { useLanguage } from "../../context/LanguageContext";

type MatchesContainerProps = {
  title?: string;
  matches: MatchListEntry[];
  setProfiles: (profiles: MatchListEntry[]) => void;
  totalCount?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
  onViewProfile: (match: MatchListEntry) => void;
  onAddToShortlist: (
    match: MatchListEntry,
    profiles: MatchListEntry[],
    setProfiles: (profiles: MatchListEntry[]) => void
  ) => void;
  onSwitchProfile: (match: MatchListEntry) => void;
  onShareProfile: (match: MatchListEntry) => void;
  onReportProfile: (match: MatchListEntry) => void;
  onManageProfile: (match: MatchListEntry) => void;
  getExtraActions?: (match: MatchListEntry) => MatchCardAction[];
  onTakeProfile?: (match: MatchListEntry) => void;
  onWhatsAppProfile?: (match: MatchListEntry) => void;
  renderActions?: (match: MatchListEntry) => ReactNode;
  viewMode?: "list" | "grid";
  onViewModeChange?: (mode: "list" | "grid") => void;
  matchCountLabel?: string;
  headerExtras?: ReactNode;
};

const MatchesContainer: React.FC<MatchesContainerProps> = ({
  title,
  matches,
  setProfiles,
  totalCount,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  onViewProfile,
  onManageProfile,
  onAddToShortlist,
  onSwitchProfile,
  onShareProfile,
  onReportProfile,
  getExtraActions,
  onTakeProfile,
  onWhatsAppProfile,
  renderActions,
  viewMode: controlledViewMode,
  onViewModeChange,
  matchCountLabel: matchCountLabelOverride,
  headerExtras,
}) => {
  const { t } = useLanguage();
  const [uncontrolledViewMode, setUncontrolledViewMode] = useState<
    "list" | "grid"
  >("list");
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const displayMatches = useMemo(() => matches, [matches]);

  
  const headerTitle = title ?? "MeeMarriage";

  const hasTotal = typeof totalCount === "number" && totalCount > 0;
  const matchCountLabel =
    matchCountLabelOverride ??
    (() => {
      const template = hasTotal
        ? t("matches.count.withTotal")
        : t("matches.count.noTotal");
      const totalValue =
        hasTotal && typeof totalCount === "number"
          ? totalCount
          : displayMatches.length;
      return template
        .replace("{visible}", String(displayMatches.length))
        .replace("{total}", String(totalValue));
    })();
  const canAutoLoad = Boolean(onLoadMore) && hasMore;
  const isControlled = typeof controlledViewMode !== "undefined";
  const viewMode = isControlled ? controlledViewMode : uncontrolledViewMode;

  const updateViewMode = (mode: "list" | "grid") => {
    if (isControlled) {
      if (mode !== controlledViewMode) {
        onViewModeChange?.(mode);
      }
    } else {
      setUncontrolledViewMode(mode);
    }
  };

  useEffect(() => {
    if (!canAutoLoad) return;
    if (typeof IntersectionObserver === "undefined") return;
    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry || !entry.isIntersecting) return;
        if (loadingMore) return;
        void onLoadMore?.();
      },
      {
        rootMargin: "200px 0px",
        threshold: 0.1,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [canAutoLoad, onLoadMore, loadingMore]);

  useEffect(() => {
    if (!canAutoLoad) return;
    const handleScroll = () => {
      if (loadingMore) return;
      if (
        window.innerHeight + window.scrollY + 200 >=
        document.documentElement.scrollHeight
      ) {
        void onLoadMore?.();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [canAutoLoad, onLoadMore, loadingMore]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-[180px]">
            <h1 className="text-sm font-bold text-pink-600">{headerTitle}</h1>
            <p className="text-sm text-gray-600">{matchCountLabel}</p>
          </div>
          <div className="ml-auto flex gap-2 rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => updateViewMode("list")}
              className={`p-2 rounded transition-all ${
                viewMode === "list"
                  ? "bg-white text-pink-600 shadow-sm"
                  : "text-gray-600 hover:text-pink-600"
              }`}
            >
              <List size={20} />
            </button>
            <button
              onClick={() => updateViewMode("grid")}
              className={`p-2 rounded transition-all ${
                viewMode === "grid"
                  ? "bg-white text-pink-600 shadow-sm"
                  : "text-gray-600 hover:text-pink-600"
              }`}
            >
              <Grid size={20} />
            </button>
          </div>
        </div>
        {headerExtras && <div className="mt-3">{headerExtras}</div>}
      </div>

      <div className="max-w-7xl mx-auto px-4">
        {displayMatches.length > 0 ? (
          <>
            {viewMode === "list" ? (
              <ListView
                matches={displayMatches}
                setProfiles={setProfiles}
                onViewProfile={onViewProfile}
                onAddToShortlist={onAddToShortlist}
                onSwitchProfile={onSwitchProfile}
                onManageProfile={onManageProfile}
                onShareProfile={onShareProfile}
                onReportProfile={onReportProfile}
                getExtraActions={getExtraActions}
                onTakeProfile={onTakeProfile}
                onWhatsAppProfile={onWhatsAppProfile}
                renderActions={renderActions}
              />
            ) : (
              <GridView
                matches={displayMatches}
                setProfiles={setProfiles}
                onViewProfile={onViewProfile}
                onAddToShortlist={onAddToShortlist}
                onSwitchProfile={onSwitchProfile}
                onShareProfile={onShareProfile}
                onManageProfile={onManageProfile}
                onReportProfile={onReportProfile}
                getExtraActions={getExtraActions}
                onTakeProfile={onTakeProfile}
                onWhatsAppProfile={onWhatsAppProfile}
                renderActions={renderActions}
              />
            )}
          </>
        ) : (
          <div className="py-12 text-center text-gray-500">
            <p>{t("matches.messages.empty")}</p>
          </div>
        )}
        {canAutoLoad && (
          <div ref={loadMoreRef} className="h-1 w-full" aria-hidden="true" />
        )}
        {canAutoLoad && hasMore && (
          <div className="py-4 text-center text-sm text-gray-500">
            {t("matches.messages.loading")}
          </div>
        )}
        {!loadingMore &&
          !hasMore &&
          hasTotal &&
          displayMatches.length >= (totalCount ?? 0) &&
          displayMatches.length > 0 && (
            <div className="py-4 text-center text-xs text-gray-400">
              {t("matches.messages.end")}
            </div>
          )}
      </div>
    </div>
  );
};

export default MatchesContainer;
